/**
 * Central registry of route chunk loaders + a hover/focus/idle prefetcher.
 *
 * WHY: WS1 splits every non-home page into its own lazy chunk. Left alone,
 * that would trade a slow first load for a per-click chunk fetch — losing the
 * "instant on every click" feel the SPA has today (everything is preloaded).
 * To keep clicks instant we warm a route's chunk BEFORE the user clicks:
 *   - on <Link> hover / focus / touchstart (intent signal), and
 *   - the highest-intent route (/instant-estimate) on requestIdleCallback
 *     after first paint.
 *
 * CRITICAL: `routeLoaders` is the single source of truth for the dynamic
 * import of each page. Routes.jsx builds its React.lazy() components from the
 * SAME function references, so a prefetch and the eventual lazy render resolve
 * to the exact same chunk (the module cache dedupes — no double download).
 */

// path -> () => import(page). Keys are the router pathnames so the delegated
// link prefetcher can look a route up straight from an <a href>.
export const routeLoaders = {
  '/terms-of-service': () => import('../pages/terms-of-service'),
  '/privacy-policy': () => import('../pages/privacy-policy'),
  '/privacy-choices': () => import('../pages/privacy-choices'),
  '/for-contractors': () => import('../pages/for-contractors'),
  '/financing-options': () => import('../pages/financing-options'),
  '/instant-estimate': () => import('../pages/instant-estimate'),
  '/support': () => import('../pages/support'),
  '/reviews': () => import('../pages/reviews'),
  '/how-it-works': () => import('../pages/how-it-works'),
  '/gallery': () => import('../pages/gallery'),
  '/warranty': () => import('../pages/warranty'),
  '/shingles': () => import('../pages/shingles'),
  '/about': () => import('../pages/about-us'),
  '/user-registration': () => import('../pages/user-registration'),
  '/user-dashboard': () => import('../pages/user-dashboard'),
  '/blog': () => import('../pages/blog'),
  '/service-areas': () => import('../pages/service-areas'),
  '/service-areas/ohio/dayton': () => import('../pages/service-areas/ohio/dayton'),
  '/service-areas/ohio/columbus': () => import('../pages/service-areas/ohio/columbus'),
  '/service-areas/ohio/cincinnati': () => import('../pages/service-areas/ohio/cincinnati'),
  '/service-areas/texas/dallas': () => import('../pages/service-areas/texas/dallas'),
  '/national-map': () => import('../pages/national-map'),
  '/admin/city-analytics': () => import('../pages/admin-city-analytics'),
  '/welcome': () => import('../pages/welcome'),
  '/pitch-deck': () => import('../pages/pitch-deck'),
  '/insurance': () => import('../pages/insurance'),
  // NOTE: '/compare' (no second segment) is a static SEO hub served straight
  // from public/compare/index.html — nginx `try_files $uri/` matches it before
  // the SPA fallback, so the interactive tool lives at /compare/quote.
  '/compare/quote': () => import('../pages/compare-quote'),
  // Shared (redacted) Quote Check report — /compare/quote/shared/:token.
  // Tokenized path, so (like /blog/:slug) the loader is keyed on the PREFIX
  // and prefetchByHref maps any tokenized href onto it.
  '/compare/quote/shared': () => import('../pages/compare-quote/shared'),
};

const prefetched = new Set();

/** Warm a single route's chunk (idempotent, best-effort). */
export function prefetchRoute(path) {
  const loader = routeLoaders[path];
  if (!loader || prefetched.has(path)) return;
  prefetched.add(path);
  // Swallow errors (offline / chunk 404 mid-deploy) and allow a later retry.
  Promise.resolve()
    .then(loader)
    .catch(() => prefetched.delete(path));
}

/** Resolve an in-app href to a known route and prefetch it. */
export function prefetchByHref(href) {
  if (!href || typeof window === 'undefined') return;
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return; // ignore external links
    if (routeLoaders[url.pathname]) prefetchRoute(url.pathname);
    // Blog posts share one chunk with the blog index; warm it on post hover.
    else if (url.pathname.startsWith('/blog/')) prefetchRoute('/blog');
    // Tokenized shared-report links all resolve to the one shared-page chunk.
    else if (url.pathname.startsWith('/compare/quote/shared/'))
      prefetchRoute('/compare/quote/shared');
  } catch {
    /* malformed href — ignore */
  }
}

/**
 * Install delegated hover/focus/touch prefetching for every in-app <a>.
 * Delegation (one listener on document) means we never have to touch the
 * dozens of <Link> call-sites across the app. Returns a cleanup fn.
 */
export function installLinkPrefetch() {
  if (typeof document === 'undefined') return () => {};
  const onIntent = (event) => {
    const target = event.target;
    const anchor = target && target.closest ? target.closest('a[href]') : null;
    if (anchor) prefetchByHref(anchor.getAttribute('href'));
  };
  const opts = { passive: true, capture: true };
  document.addEventListener('mouseover', onIntent, opts);
  document.addEventListener('focusin', onIntent, opts);
  document.addEventListener('touchstart', onIntent, opts);
  return () => {
    document.removeEventListener('mouseover', onIntent, opts);
    document.removeEventListener('focusin', onIntent, opts);
    document.removeEventListener('touchstart', onIntent, opts);
  };
}

/**
 * Viewport-based prefetch — the MOBILE half of WS1.3.
 *
 * ~90% of traffic is phones, where hover doesn't exist and `touchstart`
 * fires only milliseconds before the navigation itself (too late to hide a
 * chunk fetch on a 4G link). Instead, warm the chunk for any in-app link the
 * user can currently SEE: an IntersectionObserver watches route links and
 * schedules their loader on requestIdleCallback once they scroll into view
 * (quicklink pattern). Chunks are small post-split (~10–70 KB) and the
 * `prefetched` set dedupes, so worst case is a handful of tiny idle fetches.
 *
 * Respects constrained networks: no-ops when Save-Data is on or the
 * connection reports 2g. Returns a cleanup fn.
 */
export function installViewportPrefetch() {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    !('IntersectionObserver' in window) ||
    !('MutationObserver' in window)
  ) {
    return () => {};
  }
  // Don't burn data for users who asked us not to / can't afford it.
  const conn = navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) {
    return () => {};
  }

  const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 1500));
  const cancelIdle = window.cancelIdleCallback || window.clearTimeout;
  const idleIds = new Set();
  const seen = new WeakSet(); // anchors already handed to the observer

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target);
        const href = entry.target.getAttribute('href');
        // Defer the actual import to idle time so a visible-on-load footer
        // full of links never competes with the route's own work.
        const id = idle(() => {
          idleIds.delete(id);
          prefetchByHref(href);
        }, { timeout: 4000 });
        idleIds.add(id);
      }
    },
    // Start the fetch slightly before the link is actually on screen.
    { rootMargin: '200px' }
  );

  const isPrefetchableAnchor = (a) => {
    const href = a.getAttribute('href');
    if (!href) return false;
    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return false;
      return (
        Boolean(routeLoaders[url.pathname]) ||
        url.pathname.startsWith('/blog/') ||
        url.pathname.startsWith('/compare/quote/shared/')
      );
    } catch {
      return false;
    }
  };

  const scan = () => {
    document.querySelectorAll('a[href]').forEach((a) => {
      if (seen.has(a)) return;
      seen.add(a);
      if (isPrefetchableAnchor(a)) io.observe(a);
    });
  };

  // SPA re-renders swap link nodes constantly — re-scan (debounced) on DOM
  // mutations so links added after mount are observed too.
  let scanTimer = 0;
  const mo = new MutationObserver(() => {
    window.clearTimeout(scanTimer);
    scanTimer = window.setTimeout(scan, 500);
  });

  // First scan waits for idle so it never contends with first paint.
  const firstScanId = idle(() => {
    idleIds.delete(firstScanId);
    scan();
    mo.observe(document.body, { childList: true, subtree: true });
  }, { timeout: 3000 });
  idleIds.add(firstScanId);

  return () => {
    window.clearTimeout(scanTimer);
    idleIds.forEach((id) => cancelIdle.call(window, id));
    idleIds.clear();
    mo.disconnect();
    io.disconnect();
  };
}

/**
 * After first paint, idle-prefetch the highest-intent conversion route so the
 * first click into the funnel is instant. Returns a cleanup fn.
 */
export function prefetchHighIntentWhenIdle() {
  if (typeof window === 'undefined') return () => {};
  const run = () => prefetchRoute('/instant-estimate');
  if ('requestIdleCallback' in window) {
    const id = window.requestIdleCallback(run, { timeout: 2500 });
    return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(run, 1500);
  return () => window.clearTimeout(id);
}
